import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from 'src/components/input/Input';
import { COLLECTED_CUSTOMER_DATE_VALUES, NETWORK_NAME_LABELS, SUPPORTED_TOKENS } from 'src/utils/constants';
import Checkbox from 'src/components/checkbox/Checkbox';
import downIcon from 'src/icons/down.svg';
import './CreateMerchantForm.css';
import Button from 'src/components/button/Button';
import toast from 'react-hot-toast';
import API from 'src/utils/api';
import Loader from 'src/components/loader/Loader';
import SelectDropdown from 'src/components/select-dropdown/SelectDropdown';
import { truncate } from 'src/utils/strings';

const tokenOptions = Object.keys(SUPPORTED_TOKENS).reduce((acc, val) => {
    const newAcc = { ...acc };
    SUPPORTED_TOKENS[val].forEach((token) => {
        if (Object.keys(newAcc).includes(token)) {
            return newAcc[token].push(val);
        }
        newAcc[token] = [val];
    });
    return newAcc;
}, {});

const tokenOptionsArray = Object.keys(tokenOptions);

const CreateMerchantForm = () => {
    const navigate = useNavigate();

    const [formName, setFormName] = useState();
    const [referenceForClients, setReferenceForClients] = useState();
    const [acceptedTokens, setAcceptedTokens] = useState();
    const [collectedCustomerData, setCollectedCustomerData] = useState([]);

    const [tokenNetworksSelectOpen, setTokenNetworksSelectOpen] = useState();

    const [loading, setLoading] = useState();
    const [errors, setErrors] = useState({});

    const onCollectedCustomerDataChange = (fieldName) => {
        setCollectedCustomerData(
            collectedCustomerData.includes(fieldName)
                ? collectedCustomerData.filter((field) => field !== fieldName)
                : [...collectedCustomerData, fieldName]
        );
    };

    const onOptionChange = (selected, token, network) => {
        if (selected) {
            if (tokenOptions[token].length === 1) {
                return setAcceptedTokens({
                    ...acceptedTokens,
                    [token]: tokenOptions[token],
                });
            }
            return setAcceptedTokens({
                ...acceptedTokens,
                [token]: [...(acceptedTokens[token] ?? []), network],
            });
        }
        if (tokenOptions[token].length === 1) {
            const newAcceptedTokens = { ...acceptedTokens };
            delete newAcceptedTokens[token];
            return setAcceptedTokens(newAcceptedTokens);
        }
        setAcceptedTokens({
            ...acceptedTokens,
            [token]: acceptedTokens[token].filter((o) => o !== network),
        });
    };

    const onOptionClick = (option) => {
        if (tokenNetworksSelectOpen === option) return setTokenNetworksSelectOpen(null);
        if (tokenOptions[option].length > 1) return setTokenNetworksSelectOpen(option);
        const isOptionSelected = acceptedTokens && Object.keys(acceptedTokens).includes(option);
        onOptionChange(!isOptionSelected, option);
    };

    const checkAndSetErrors = useCallback(() => {
        const newErrors = {};
        if (!formName || formName.length > 255) newErrors.formName = 'Please enter a valid name (max 255 characters)';
        if (!referenceForClients || referenceForClients.length > 255)
            newErrors.referenceForClients = 'Please enter a valid reference (max 255 characters)';
        if (!acceptedTokens || Object.keys(acceptedTokens).length === 0) {
            newErrors.acceptedTokens = 'Please select at least 1 token';
        }
        setErrors(newErrors);
        return newErrors;
    }, [acceptedTokens, formName, referenceForClients]);

    useEffect(() => {
        if (formName === undefined || referenceForClients === undefined || !acceptedTokens) return;
        checkAndSetErrors();
    }, [formName, referenceForClients, acceptedTokens, collectedCustomerData, checkAndSetErrors]);

    const onSubmit = async (e) => {
        e.preventDefault();
        const newErrors = checkAndSetErrors();
        if (Object.keys(newErrors).length !== 0) return;
        setLoading(true);
        try {
            const response = await API.post('/payment-forms/create', {
                name: formName,
                clientReference: referenceForClients,
                acceptedTokens: JSON.stringify(acceptedTokens),
                collectedCustomerData,
            });
            if (response.status === 200) {
                toast.success('Your payment form has been created successfully');
                setTimeout(() => {
                    setLoading(false);
                    navigate('/merchant');
                }, 3000);
            }
        } catch (error) {
            setLoading(false);
            toast.error('Failed to create form');
        }
    };

    if (loading) {
        return (
            <div className="px-10 pt-24">
                <Loader />
            </div>
        );
    }

    return (
        <div className="px-4 md:px-10 pt-24">
            <span className="text-2xl font-bold block w-full text-center">New payment form</span>
            <form className="lg:w-1/2 mx-auto mt-8" onSubmit={onSubmit}>
                <Input
                    value={formName}
                    setValue={setFormName}
                    fieldName="formName"
                    label="Name"
                    placeholder="My Shop"
                    error={errors.formName}
                />
                <Input
                    value={referenceForClients}
                    setValue={setReferenceForClients}
                    fieldName="referenceForClients"
                    label="Reference for clients (will appear on their transactions list)"
                    placeholder="my-shop.com"
                    error={errors.referenceForClients}
                />
                <SelectDropdown
                    label="Accepted tokens for payments"
                    placeholder="Accepted tokens"
                    text={acceptedTokens && truncate(Object.keys(acceptedTokens).join(', '), 14)}
                    showPlaceholder={!acceptedTokens || !Object.keys(acceptedTokens).length}
                    contentClassName="bg-white drop-shadow-md"
                >
                    {() =>
                        tokenOptionsArray.map((option, i) => (
                            <div key={i}>
                                <button
                                    className="flex justify-between items-center w-full py-3 px-4"
                                    onClick={() => onOptionClick(option)}
                                    type="button"
                                >
                                    <span className="flex items-center">
                                        <img
                                            src={`/crypto-icons/${option}.svg`}
                                            alt={option}
                                            width={25}
                                            className="mr-2"
                                        />
                                        {option}
                                    </span>
                                    {tokenOptions[option].length === 1 ? (
                                        <Checkbox
                                            fieldName={option}
                                            checked={acceptedTokens && Object.keys(acceptedTokens).includes(option)}
                                            onChange={(selected) => onOptionChange(selected, option)}
                                        />
                                    ) : (
                                        <img
                                            src={downIcon}
                                            alt="down arrow"
                                            className={tokenNetworksSelectOpen === option ? 'rotate-180' : ''}
                                        />
                                    )}
                                </button>
                                {tokenNetworksSelectOpen === option &&
                                    tokenOptions[option].map((network, j) => (
                                        <div key={j}>
                                            <button
                                                className="flex justify-between items-center w-full py-3 px-4 pl-12"
                                                onClick={(e) => onOptionChange(e, option, network)}
                                                type="button"
                                            >
                                                <span className="flex items-center text-gray-darker">
                                                    <img
                                                        src={`/crypto-icons/${SUPPORTED_TOKENS[network][0]}.svg`}
                                                        alt={option}
                                                        width={20}
                                                        className="mr-2"
                                                    />
                                                    {option} on the {NETWORK_NAME_LABELS[network]} network
                                                </span>
                                                <Checkbox
                                                    fieldName={option}
                                                    checked={
                                                        acceptedTokens &&
                                                        Object.keys(acceptedTokens).includes(option) &&
                                                        acceptedTokens[option].includes(network)
                                                    }
                                                    onChange={(selected) => onOptionChange(selected, option, network)}
                                                />
                                            </button>
                                            {j !== tokenOptions[option].length - 1 && <div className="separator"></div>}
                                        </div>
                                    ))}
                                {i !== tokenOptionsArray.length - 1 && <div className="separator"></div>}
                            </div>
                        ))
                    }
                </SelectDropdown>
                <span className="text-lg mt-8 mb-4 block">Select the data you want to collect from your clients</span>
                <label className="flex items-center">
                    <Checkbox
                        className="mr-2"
                        checked={collectedCustomerData.includes(COLLECTED_CUSTOMER_DATE_VALUES.EMAIL)}
                        onChange={() => onCollectedCustomerDataChange(COLLECTED_CUSTOMER_DATE_VALUES.EMAIL)}
                    />
                    Email
                </label>
                <label className="flex items-center mt-2">
                    <Checkbox
                        className="mr-2"
                        checked={collectedCustomerData.includes(COLLECTED_CUSTOMER_DATE_VALUES.FIRST_NAME)}
                        onChange={() => onCollectedCustomerDataChange(COLLECTED_CUSTOMER_DATE_VALUES.FIRST_NAME)}
                    />
                    First Name
                </label>
                <label className="flex items-center mt-2">
                    <Checkbox
                        className="mr-2"
                        checked={collectedCustomerData.includes(COLLECTED_CUSTOMER_DATE_VALUES.LAST_NAME)}
                        onChange={() => onCollectedCustomerDataChange(COLLECTED_CUSTOMER_DATE_VALUES.LAST_NAME)}
                    />
                    Last Name
                </label>
                <label className="flex items-center mt-2">
                    <Checkbox
                        className="mr-2"
                        checked={collectedCustomerData.includes(COLLECTED_CUSTOMER_DATE_VALUES.ADDRESS)}
                        onChange={() => onCollectedCustomerDataChange(COLLECTED_CUSTOMER_DATE_VALUES.ADDRESS)}
                    />
                    Address
                </label>
                <label className="flex items-center mt-2">
                    <Checkbox
                        className="mr-2"
                        checked={collectedCustomerData.includes(COLLECTED_CUSTOMER_DATE_VALUES.NOTES)}
                        onChange={() => onCollectedCustomerDataChange(COLLECTED_CUSTOMER_DATE_VALUES.NOTES)}
                    />
                    Notes (a text area where the client can type anything)
                </label>

                <Button text="Create" primary className="w-full mt-10" type="submit" />
            </form>
        </div>
    );
};

export default CreateMerchantForm;
